import { useMemo, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, Boxes, Package, Beaker, ShieldAlert, AlertOctagon,
  Wrench, Users, Building2, Settings, ScanLine, Stethoscope, Activity, Droplets, ClipboardCheck,
  MapPin, Truck, Tag, Layers, FlaskConical, Hash, Network, BookOpen, ChevronDown, Hand,
} from 'lucide-react';
import { Topbar } from './Topbar';
import { useAuth, type Role } from '@/shared/auth/AuthProvider';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles?: Role[];
  section: 'main' | 'ops' | 'admin';
}

const NAV: NavItem[] = [
  { to: '/',              label: 'Visão geral',     icon: LayoutDashboard, section: 'main' },
  { to: '/ajuda',         label: 'Ajuda',           icon: BookOpen,        section: 'main' },
  { to: '/sala-suja',     label: 'Sala suja',       icon: Hand,            section: 'main' },
  { to: '/expurgo',       label: 'Expurgo',         icon: ScanLine,        section: 'ops' },
  { to: '/lavagem',       label: 'Lavagem',         icon: Droplets,        section: 'ops' },
  { to: '/preparo',       label: 'Preparo',         icon: Package,         section: 'ops' },
  { to: '/esterilizacao', label: 'Esterilização',   icon: Beaker,          section: 'ops' },
  { to: '/liberacao',     label: 'Liberação',       icon: ClipboardCheck,  section: 'ops', roles: ['ADMIN','SUPERVISOR','NURSE_RT'] },
  { to: '/arsenal',       label: 'Arsenal (FIFO)',  icon: Boxes,           section: 'ops' },
  { to: '/cirurgia',      label: 'Point-of-Care',   icon: Stethoscope,     section: 'ops' },
  { to: '/quarentena',    label: 'Quarentena',      icon: ShieldAlert,     section: 'ops' },
  { to: '/recall',        label: 'Recall',          icon: AlertOctagon,    section: 'ops', roles: ['ADMIN','SUPERVISOR','NURSE_RT'] },
  { to: '/manutencao',    label: 'Manutenção',      icon: Wrench,          section: 'ops', roles: ['ADMIN','SUPERVISOR','NURSE_RT'] },
  { to: '/equipamentos',  label: 'Equipamentos',    icon: Wrench,          section: 'admin', roles: ['ADMIN','SUPERVISOR','NURSE_RT'] },
  { to: '/item-models',   label: 'Modelos de Item', icon: Tag,             section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/itens',         label: 'Inventário',      icon: Hash,            section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/kits',          label: 'Kits (templates)',icon: Layers,          section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/embalagens',    label: 'Embalagens',      icon: Package,         section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/especialidades',label: 'Especialidades',  icon: FlaskConical,    section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/setores',       label: 'Setores',         icon: Building2,       section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/locais',        label: 'Locais',          icon: MapPin,          section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/fornecedores',  label: 'Fornecedores',    icon: Truck,           section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/usuarios',      label: 'Usuários',        icon: Users,           section: 'admin', roles: ['ADMIN'] },
  { to: '/organizacao',   label: 'Organização',     icon: Building2,       section: 'admin', roles: ['ADMIN'] },
  { to: '/filiais',       label: 'Filiais',         icon: Network,         section: 'admin', roles: ['ADMIN'] },
  { to: '/auditoria',     label: 'Auditoria',       icon: Activity,        section: 'admin', roles: ['ADMIN','SUPERVISOR'] },
  { to: '/configuracoes', label: 'Configurações',   icon: Settings,        section: 'admin', roles: ['ADMIN'] },
];

type GroupKey = 'main' | 'ops' | 'admin';

const SIDEBAR_COLLAPSED_KEY = 'ictus.sidebar.collapsed';
const SIDEBAR_GROUPS_KEY = 'ictus.sidebar.groups';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function readSidebarGroups() {
  const fallback: Record<GroupKey, boolean> = { main: true, ops: true, admin: false };
  try {
    const raw = localStorage.getItem(SIDEBAR_GROUPS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Record<GroupKey, boolean>>;
    return {
      main: parsed.main ?? fallback.main,
      ops: parsed.ops ?? fallback.ops,
      admin: parsed.admin ?? fallback.admin,
    };
  } catch {
    return fallback;
  }
}

function saveSidebarCollapsed(v: boolean) {
  try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, v ? '1' : '0'); } catch { /* no-op */ }
}

function saveSidebarGroups(v: Record<GroupKey, boolean>) {
  try { localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(v)); } catch { /* no-op */ }
}

function NavGroup({
  title,
  items,
  collapsed,
  open,
  onToggle,
}: {
  title: string;
  items: NavItem[];
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  if (!items.length) return null;
  return (
    <div className="px-2 py-2">
      <button
        type="button"
        onClick={onToggle}
        className="ict-focus-ring flex w-full items-center justify-between rounded px-2 pb-1 text-left text-xxs font-semibold uppercase tracking-wider text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700"
      >
        <span>{title}</span>
        {!collapsed ? (
          <ChevronDown
            size={14}
            className={[open ? 'rotate-0' : '-rotate-90', 'transition-transform'].join(' ')}
          />
        ) : null}
      </button>
      {open ? (
        <ul className="flex flex-col gap-0.5">
        {items.map(({ to, label, icon: Icon }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={to === '/'}
              className={({ isActive }) => [
                'ict-focus-ring flex items-center gap-2 rounded px-2 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                  : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800',
              ].join(' ')}
            >
                <Icon size={16} strokeWidth={1.75} />
                {!collapsed ? <span className="truncate">{label}</span> : null}
            </NavLink>
          </li>
        ))}
        </ul>
      ) : null}
    </div>
  );
}

export function AppShell() {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [openGroups, setOpenGroups] = useState(readSidebarGroups);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      saveSidebarCollapsed(next);
      return next;
    });
  };

  const toggleGroup = (key: GroupKey) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveSidebarGroups(next);
      return next;
    });
  };

  const filter = (s: NavItem['section']) =>
    NAV.filter((n) => n.section === s && (!n.roles || (user && n.roles.includes(user.role))));

  const sidebarClass = useMemo(
    () => [
      'hidden shrink-0 border-r border-neutral-200 bg-neutral-0 md:flex md:flex-col',
      'dark:border-neutral-700 dark:bg-neutral-800 transition-all',
      sidebarCollapsed ? 'w-20' : 'w-60',
    ].join(' '),
    [sidebarCollapsed],
  );

  return (
    <div className="flex h-full min-h-screen flex-col">
      <Topbar sidebarCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar} />
      <div className="flex min-h-0 flex-1">
        <aside className={sidebarClass}>
          <nav className="flex-1 overflow-y-auto">
            <NavGroup title="Painel" items={filter('main')} collapsed={sidebarCollapsed} open={openGroups.main} onToggle={() => toggleGroup('main')} />
            <NavGroup title="Operacional" items={filter('ops')} collapsed={sidebarCollapsed} open={openGroups.ops} onToggle={() => toggleGroup('ops')} />
            <NavGroup title="Administração" items={filter('admin')} collapsed={sidebarCollapsed} open={openGroups.admin} onToggle={() => toggleGroup('admin')} />
          </nav>
          <div className="border-t border-neutral-200 px-3 py-2 text-xxs text-neutral-500 dark:border-neutral-700">
            {sidebarCollapsed ? 'v0.2' : 'ICTUS CME · v0.2'}
          </div>
        </aside>
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
